import { supabase } from '@/integrations/supabase/client';

/**
 * Delete a document from Supabase storage
 */
export async function deleteDocumentFromStorage(
  fileUrl: string,
  bucketName: string = 'knowledge-sources'
): Promise<{ success: boolean; error?: string }> {
  try {
    // Extract file path from public URL
    const urlParts = fileUrl.split(`/storage/v1/object/public/${bucketName}/`);
    if (urlParts.length < 2) {
      return { success: false, error: 'Invalid file URL' };
    }
    
    const filePath = decodeURIComponent(urlParts[1]);
    
    const { error } = await supabase.storage
      .from(bucketName)
      .remove([filePath]);
    
    if (error) {
      console.error('Storage deletion error:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (err) {
    console.error('Delete document error:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export interface ParsedKnowledgeSource {
  name: string;
  url: string;
  extracted_text?: string;
  word_count?: number;
}

/**
 * Parse a document and extract text content using the parse-document edge function
 */
export async function parseDocument(fileUrl: string, fileName: string): Promise<{
  extracted_text: string;
  word_count: number;
  success: boolean;
  warning?: string;
  error?: string;
}> {
  try {
    const { data, error } = await supabase.functions.invoke('parse-document', {
      body: { fileUrl, fileName }
    });

    if (error) {
      console.error('Parse document error:', error);
      // Return partial success - file may still be uploaded
      return {
        extracted_text: '',
        word_count: 0,
        success: false,
        error: error.message || 'Failed to parse document'
      };
    }

    // Handle graceful degradation - success with warning means file uploaded but text extraction had issues
    if (data.success) {
      return {
        extracted_text: data.extracted_text || '',
        word_count: data.word_count || 0,
        success: true,
        warning: data.warning
      };
    }

    return {
      extracted_text: '',
      word_count: 0,
      success: false,
      error: data.error || 'Failed to parse document'
    };
  } catch (err) {
    console.error('Document parsing error:', err);
    return {
      extracted_text: '',
      word_count: 0,
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error'
    };
  }
}

/**
 * Upload a file to storage and parse its content
 */
export async function uploadAndParseDocument(
  file: File,
  bucketName: string = 'knowledge-sources',
  pathPrefix: string = 'knowledge-sources'
): Promise<{
  name: string;
  url: string;
  extracted_text?: string;
  word_count?: number;
  success: boolean;
  warning?: string;
  error?: string;
}> {
  try {
    // Sanitize filename
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${pathPrefix}/${Date.now()}-${sanitizedName}`;
    
    // Upload file to storage first - this is the priority
    const { error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(fileName, file);
    
    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(bucketName)
      .getPublicUrl(fileName);
    
    // Parse document to extract text - this can fail gracefully
    const parseResult = await parseDocument(publicUrl, file.name);
    
    // Even if parsing fails, the file is uploaded successfully
    return {
      name: file.name,
      url: publicUrl,
      extracted_text: parseResult.extracted_text || '',
      word_count: parseResult.word_count || 0,
      success: true,
      warning: parseResult.warning || (parseResult.success ? undefined : 'Text extraction failed - file saved without content preview')
    };
  } catch (err) {
    console.error('Upload and parse error:', err);
    return {
      name: file.name,
      url: '',
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error'
    };
  }
}
